import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Btn, Badge } from "@/components/ui-kit";
import { Compass, MapPin, RefreshCw, ChevronRight } from "lucide-react";

export default function ClosestTargets() {
  const [targets, setTargets] = useState([]);
  const [kind, setKind] = useState("pharmacies");
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const refresh = async () => {
    setErr(""); setLoading(true);
    if (!navigator.geolocation) { setErr("Geolocation not supported"); setLoading(false); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(c);
        try {
          const { data } = await api.get("/insights/closest-targets", {
            params: { lat: c.lat, lng: c.lng, kind, top_n: 5 },
          });
          setTargets(data);
        } catch (e) { setErr(e.message); }
        finally { setLoading(false); }
      },
      (er) => { setErr(er.message || "Could not get location. Use a real GPS device."); setLoading(false); },
      { timeout: 8000, enableHighAccuracy: true }
    );
  };

  useEffect(() => { refresh(); }, [kind]);

  const navigate = (t) => {
    const url = `https://www.openstreetmap.org/?mlat=${t.latitude}&mlon=${t.longitude}#map=17/${t.latitude}/${t.longitude}`;
    window.open(url, "_blank");
  };

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white inline-flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Compass className="w-5 h-5 text-blue-700"/>
            Closest High-Value Targets
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Unvisited 14+ days · sorted by distance</p>
        </div>
        <button onClick={refresh} disabled={loading} className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}/>
        </button>
      </div>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setKind("pharmacies")} className={`text-xs font-semibold px-3 py-1 rounded-full ${kind === "pharmacies" ? "bg-blue-700 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"}`}>
          A+/A Pharmacies
        </button>
        <button onClick={() => setKind("doctors")} className={`text-xs font-semibold px-3 py-1 rounded-full ${kind === "doctors" ? "bg-blue-700 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"}`}>
          KOL-A/B Doctors
        </button>
      </div>

      {err && <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded p-2 mb-3">{err}</div>}

      <div className="space-y-2">
        {targets.length > 0 ? targets.map((t, i) => (
          <div key={t.id} className="flex items-center gap-3 p-2.5 rounded-md border border-slate-200 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-700 transition-colors">
            <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 grid place-items-center font-bold text-xs">
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-slate-900 dark:text-white truncate">{t.name}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{t.area} · {t.city}</div>
            </div>
            <Badge color={t.classification === "A+" || t.classification === "KOL-A" ? "blue" : "purple"}>{t.classification}</Badge>
            <div className="text-xs text-slate-600 dark:text-slate-300 font-mono font-semibold">{t.distance_km} km</div>
            <button onClick={() => navigate(t)} className="p-1 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded text-blue-700 dark:text-blue-400" title="Open in map">
              <MapPin className="w-4 h-4"/>
            </button>
          </div>
        )) : !loading && !err && (
          <div className="text-center py-6 text-sm text-slate-400">
            No nearby unvisited targets. Add GPS coordinates to your doctors/pharmacies.
          </div>
        )}
        {loading && (
          <div className="text-center py-6 text-sm text-slate-400">Locating you...</div>
        )}
      </div>
    </Card>
  );
}
